import type {
  GridRenderHeaderFilterProps,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { HeaderCell } from "src/components/DataGrid/HeaderCell.tsx";
import { DataGridDateRangeHeaderFilter } from "src/components/DataGrid/DataGridDateRangeHeaderFilter.tsx";
import { DataGridSelectHeaderFilter } from "src/components/DataGrid/DataGridSelectHeaderFilter.tsx";
import { getDataGridSelectFilterOperators } from "src/components/DataGrid/getDataGridSelectFilterOperators.tsx";
import { DataGridNumberHeaderFilter } from "src/components/DataGrid/DataGridNumberHeaderFilter.tsx";
import { getDataGridNumberFilterOperators } from "src/components/DataGrid/getDataGridNumberFilterOperators.tsx";
import { getDataGridTextFilterOperators } from "src/components/DataGrid/getDataGridTextFilterOperators.tsx";
import React from "react";
import type { GridColDef } from "src/components/DataGrid/DataGrid.tsx";
import type { GridColDef as BaseGridColDef } from "@mui/x-data-grid-premium";
import { DataGridMultiSelectHeaderFilter } from "src/components/DataGrid/DataGridMultiSelectHeaderFilter.tsx";
import { getDataGridMultiSelectFilterOperators } from "src/components/DataGrid/getDataGridMultiSelectFilterOperators.tsx";
import { DataGridTextHeaderFilter } from "src/components/DataGrid/DataGridTextHeaderFilter.tsx";
import { getResourceFieldOptions } from "src/system/getResourceFieldOptions.ts";
import type { TFunction } from "i18next";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { ThemeProvider } from "@mui/material";
import { useNonConflictingTheme } from "src/lib/emotion/use-non-conflicting-theme.ts";
import { DataGridInfiniteSelectHeaderFilter } from "src/components/DataGrid/DataGridInfiniteSelectHeaderFilter.tsx";
const COLUMN_MIN_WIDTH = 100;

const DEFAULT_TEXT_COLUMN_WIDTH = 200;
const DEFAULT_DATE_COLUMN_WIDTH = 275;

export const convertDataGridColumns = <
  R extends GridValidRowModel = GridValidRowModel,
>(
  passedColumns: GridColDef<R>[],
  t: TFunction,
): BaseGridColDef<R>[] => {
  return passedColumns.map((column) => {
    const {
      field,
      type = "string",
      headerName,
      headerFilterSelectProps,
      defaultFilterOperator,
      renderCell,
      ...rest
    } = column;

    if (type === "dateRange") {
      const dateRangerFilterOperators = [
        {
          getApplyFilterFn: () => null,
          label: "range between",
          value: "is",
          headerLabel: "range between",
        },
      ];

      return {
        field: field,
        type: "custom" as const,
        width: DEFAULT_DATE_COLUMN_WIDTH,
        minWidth: COLUMN_MIN_WIDTH,
        renderHeader: () => <HeaderCell>{headerName}</HeaderCell>,
        renderCell: renderCellWrapper({
          renderCell,
        }),
        headerName,

        renderHeaderFilter: DataGridDateRangeHeaderFilter,
        filterOperators: dateRangerFilterOperators,
        ...rest,
      };
    }

    if (type === "date") {
      return {
        field: field,
        type: "date" as const,
        width: DEFAULT_DATE_COLUMN_WIDTH,
        minWidth: COLUMN_MIN_WIDTH,

        renderHeader: () => <HeaderCell>{headerName}</HeaderCell>,
        renderCell: renderCellWrapper({
          renderCell,
        }),
        headerName,
        ...rest,
      };
    }

    if (type === "dateTime") {
      return {
        field: field,
        type: "dateTime" as const,
        width: DEFAULT_DATE_COLUMN_WIDTH,
        minWidth: COLUMN_MIN_WIDTH,

        renderHeader: () => <HeaderCell>{headerName}</HeaderCell>,
        renderCell: renderCellWrapper({
          renderCell,
        }),
        headerName,
        ...rest,
      };
    }

    if (type === "multiSelect") {
      const MultiSelectHeaderFilter = (props: GridRenderHeaderFilterProps) => {
        return (
          <DataGridMultiSelectHeaderFilter
            {...props}
            {...headerFilterSelectProps}
          />
        );
      };

      return {
        field: field,
        type: "custom" as const,
        width: DEFAULT_TEXT_COLUMN_WIDTH,
        minWidth: COLUMN_MIN_WIDTH,
        renderHeaderFilter: MultiSelectHeaderFilter,
        filterOperators: getDataGridMultiSelectFilterOperators(),

        renderHeader: () => <HeaderCell>{column.headerName}</HeaderCell>,
        renderCell: renderCellWrapper({
          renderCell,
        }),
        headerName,
        ...rest,
      };
    }

    if (type === "singleSelect") {
      const SelectHeaderFilter = (props: GridRenderHeaderFilterProps) => {
        return (
          <DataGridSelectHeaderFilter {...props} {...headerFilterSelectProps} />
        );
      };

      return {
        field: field,
        type: "singleSelect" as const,
        width: DEFAULT_TEXT_COLUMN_WIDTH,
        minWidth: COLUMN_MIN_WIDTH,
        renderHeaderFilter: SelectHeaderFilter,
        filterOperators: getDataGridSelectFilterOperators(),
        valueFormatter: (value: string) => {
          if (
            headerFilterSelectProps?.options &&
            "field" in headerFilterSelectProps.options
          ) {
            return getResourceFieldOptions({
              ...headerFilterSelectProps?.options,
              t,
            }).find((option) => option.value === value)?.label;
          }
          return value;
        },

        renderHeader: () => <HeaderCell>{column.headerName}</HeaderCell>,
        renderCell: renderCellWrapper({
          renderCell,
        }),
        headerName,
        ...rest,
      };
    }

    if (type === "infiniteSelect") {
      const SelectHeaderFilter = (props: GridRenderHeaderFilterProps) => {
        return (
          <DataGridInfiniteSelectHeaderFilter
            {...props}
            {...headerFilterSelectProps}
          />
        );
      };
      return {
        field: field,
        type: "custom" as const,
        width: DEFAULT_TEXT_COLUMN_WIDTH,
        minWidth: COLUMN_MIN_WIDTH,
        renderHeaderFilter: renderHeaderFilterWrapper({
          renderHeaderFilter: SelectHeaderFilter,
        }),
        filterOperators: getDataGridSelectFilterOperators(),
        valueFormatter: (value: string) => {
          if (
            headerFilterSelectProps?.options &&
            "field" in headerFilterSelectProps.options
          ) {
            return getResourceFieldOptions({
              ...headerFilterSelectProps?.options,
              t,
            }).find((option) => option.value === value)?.label;
          }
          return value;
        },

        renderHeader: () => <HeaderCell>{column.headerName}</HeaderCell>,
        renderCell: renderCellWrapper({
          renderCell,
        }),
        headerName,
        ...rest,
      };
    }

    if (type === "number") {
      return {
        field: field,
        // I am converting number type to string
        // because datagrid filter model doesn't allow to store non-numeric values for column of type number,
        // but we need to store somehow string, for example 1 bis 100
        type: "string" as const,
        width: DEFAULT_TEXT_COLUMN_WIDTH,
        minWidth: COLUMN_MIN_WIDTH,
        renderHeaderFilter: DataGridNumberHeaderFilter,
        filterOperators: getDataGridNumberFilterOperators(),
        renderHeader: () => <HeaderCell>{headerName}</HeaderCell>,
        renderCell: renderCellWrapper({
          renderCell,
        }),
        headerName,
        ...rest,
      };
    }
    const textFilterOperators = getDataGridTextFilterOperators();

    const textFilterOperatorsWithoutDefault = textFilterOperators.filter(
      (f) => f.value !== defaultFilterOperator,
    );
    return {
      field: field,
      type: type,
      width: DEFAULT_TEXT_COLUMN_WIDTH,
      minWidth: COLUMN_MIN_WIDTH,
      renderHeaderFilter: (props) => (
        <DataGridTextHeaderFilter
          {...props}
          defaultFilterOperator={defaultFilterOperator}
        />
      ),
      filterOperators: defaultFilterOperator
        ? [
            textFilterOperators.find((f) => f.value === defaultFilterOperator)!,
            ...textFilterOperatorsWithoutDefault,
          ]
        : textFilterOperators,
      renderHeader: () => <HeaderCell>{headerName}</HeaderCell>,
      renderCell: renderCellWrapper({
        renderCell,
      }),
      headerName,
      ...rest,
    };
  });
};

const renderCellWrapper = ({
  renderCell,
}: {
  renderCell?: (params: GridRenderCellParams) => React.ReactNode;
}) => {
  if (!renderCell) {
    return undefined;
  }
  return (params: GridRenderCellParams) => {
    const theme = useNonConflictingTheme();
    return <ThemeProvider theme={theme}>{renderCell(params)}</ThemeProvider>;
  };
};

const renderHeaderFilterWrapper = ({
  renderHeaderFilter,
}: {
  renderHeaderFilter?: (params: GridRenderHeaderFilterProps) => React.ReactNode;
}) => {
  if (!renderHeaderFilter) {
    return undefined;
  }
  return (params: GridRenderHeaderFilterProps) => {
    const theme = useNonConflictingTheme();
    return (
      <ThemeProvider theme={theme}>{renderHeaderFilter(params)}</ThemeProvider>
    );
  };
};
